import * as THREE from "three";
import Sizes from "./Utils/Sizes.js";
import Time from "./Utils/Time.js";
import Camera from "./Camera.js";
import Renderer from "./Renderer.js";
import Mesh from "./Mesh.js";
import data from "../../static/data/data";
import Scroll from "./Scroll.js";
import Text from "./Text.js";
import gsap from "gsap";

let instance = null;

export default class Experience {
	constructor(_canvas) {
		if (instance) {
			// biome-ignore lint/correctness/noConstructorReturn: <explanation>
			return instance;
		}
		instance = this;

		this.canvas = _canvas;

		this.sizes = new Sizes();
		this.time = new Time();
		this.scene = new THREE.Scene();
		this.scroll = new Scroll();
		this.camera = new Camera();
		this.renderer = new Renderer();

		this.meshes = [];
		this.texts = [];
		this.datesTexts = [];
		let positionMesh = 0;
		const spaceMesh = 5;
		data.map((items, id) => {
			const mesh = new Mesh(positionMesh, id);

			mesh.mesh.userData = {
				slug: items.slug,
			};

			this.meshes.push(mesh);

			positionMesh += spaceMesh;

			this.texts.push(new Text(this, positionMesh, items.name, { size: 0.08, y: -1.4, id }));
			this.datesTexts.push(new Text(this, positionMesh, items.date, { size: 0.04, y: -1.5, id }));

			this.scene.add(mesh.mesh);
		});

		this.raycaster = new THREE.Raycaster();
		this.mouse = new THREE.Vector2();

		this._pointerMoveHandler = (event) => {
			this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
			this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

			this.raycaster.setFromCamera(this.mouse, this.camera.instance);

			const transition = document.querySelector(".active")
			const intersects = this.raycaster.intersectObjects(this.meshes.map((m) => m.mesh));
			if (!transition) {
				document.body.style.cursor = intersects.length ? "pointer" : "default";
			} else {
				document.body.style.cursor = "default"
			}
		};

		this._clickHandler = (event) => {
			this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
			this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

			this.raycaster.setFromCamera(this.mouse, this.camera.instance);

			const transition = document.querySelector(".active")

			const intersects = this.raycaster.intersectObjects(this.meshes.map((m) => m.mesh));

			if (!transition && intersects.length > 0) {
				const clickedMesh = intersects[0].object;
				const slug = clickedMesh.userData.slug;

				if (slug) {
					gsap.to(".nav-left", { opacity: 0 });
					gsap.to(".nav-right", { opacity: 0 });

					this.meshes.forEach((mesh, id) => {
						gsap.to(mesh.mesh.position, {
							z: -5,
							duration: 0.4,
							ease: "power4.out",
							delay: id * 0.1,
						});
						gsap.to(mesh.mesh.material.uniforms.uOpacity, {
							value: 0,
							duration: 0.4,
							ease: "power4.out",
							delay: id * 0.1,
						});
					});

					this.texts.forEach((text, id) => {
						gsap.to(text.mesh.position, {
							z: -5,
							duration: 0.6,
							ease: "power4.out",
							delay: id * 0.1,
						});
						gsap.to(text.mesh.material, {
							opacity: 0,
							duration: 0.6,
							ease: "power4.out",
							delay: id * 0.1,
						});
					});

					this.datesTexts.forEach((dateText, id) => {
						gsap.to(dateText.mesh.position, {
							z: -5,
							duration: 0.6,
							ease: "power4.out",
							delay: id * 0.1,
						});
						gsap.to(dateText.mesh.material, {
							opacity: 0,
							duration: 0.6,
							ease: "power4.out",
							delay: id * 0.1,
						});
					});

					gsap.to(".scrollPercentage-scrollDown", {
						opacity: 0,
						duration: 0.6,
						ease: "power4.out",
					});
					gsap.to(".scrollPercentage-scroll", {
						opacity: 0,
						duration: 0.6,
						ease: "power4.out",
					});

					animateTransition().then(() => {
						window.location.href = `/pages/${slug}.html`;
					});
				}
			}
		};

		window.addEventListener("pointermove", this._pointerMoveHandler);
		window.addEventListener("click", this._clickHandler);
		this.sizes.on("resize", () => {
			this.resize();
		});

		this.time.on("tick", () => {
			this.update();
		});
	}

	destroy() {
		this.time.off("tick");
		this.sizes.off("resize");

		for (const mesh of this.meshes) {
			mesh.mesh.geometry.dispose();
			mesh.mesh.material.dispose();
			this.scene.remove(mesh.mesh);
		}

		for (const text of this.texts) {
			text.mesh.geometry.dispose();
			text.mesh.material.dispose();
			this.scene.remove(text.mesh);
		}

		for (const dateText of this.datesTexts) {
			dateText.mesh.geometry.dispose();
			dateText.mesh.material.dispose();
			this.scene.remove(dateText.mesh);
		}

		this.renderer.instance.dispose();

		window.removeEventListener("click", this._clickHandler);
		window.removeEventListener("pointermove", this._pointerMoveHandler);
		instance = null;
	}

	resize() {
		this.camera.resize();
		this.renderer.resize();
	}

	update() {
		this.camera.update();
		this.renderer.update();
		this.scroll.update(this.time.elapsed);

		for (const mesh of this.meshes) {
			mesh.update();
		}
	}
}

function animateTransition() {
	return new Promise((resolve) => {
		gsap.set(".block", { visibility: "visible", scaleY: 0 });
		gsap.to(
			".block-reveal-box-text",
			{
				duration: 1,
				ease: "power4.inOut",
				y: 0,
			},
			0,
		);
		gsap.to(".block", {
			scaleY: 1,
			duration: 1,
			stagger: {
				each: 0.1,
				from: "start",
				axis: "x",
			},
			ease: "power4.inOut",
			onComplete: resolve,
		});
	});
}
